"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button, Input } from "@repo/ui";
import { useUser } from "../contexts/UserContext";
import { Eye, EyeOff, AlertCircle, Check, X } from "lucide-react";
import { clientLogger } from "@repo/observability/client";
import { getReferralData } from "../lib/referral-tracking";

interface SignupFormProps {
  redirectTo?: string;
  onSuccess?: () => void;
  defaultValues?: {
    firstName?: string;
    email?: string;
    timezone?: string;
  };
  referralData?: {
    isPaidReferrer: boolean;
    referralSource: string;
  };
  promoCode?: string;
}

interface PasswordStrength {
  score: number;
  requirements: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

export function SignupForm({ redirectTo = "/onboarding", onSuccess, defaultValues, referralData, promoCode }: SignupFormProps) {
  const router = useRouter();
  const { loadProfile } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [userIP, setUserIP] = useState<string | null>(null);

  // If referral data not passed in props, get it directly (for standalone signup pages)
  const actualReferralData = referralData || getReferralData();

  // Never fall back to browser-detected timezone — see UserContext comment.
  // If defaultValues.timezone (from the quiz answer) isn't provided, leave it null.
  
  const [formData, setFormData] = useState({
    email: defaultValues?.email || "",
    password: "",
    confirmPassword: "",
    firstName: defaultValues?.firstName || ""
  });
  
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [validation, setValidation] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    terms: ""
  });

  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    requirements: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false
    }
  });

  const checkPasswordStrength = (password: string): PasswordStrength => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    let score = 0;
    if (requirements.length) score++;
    if (requirements.uppercase) score++;
    if (requirements.lowercase) score++;
    if (requirements.number) score++;
    if (requirements.special) score++;

    return { score, requirements };
  };

  useEffect(() => {
    if (formData.password) {
      setPasswordStrength(checkPasswordStrength(formData.password));
    }
  }, [formData.password]);

  // Fetch user's IP address on component mount
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIP(data.ip);
        console.log('[SignupForm] User IP detected:', data.ip);
      } catch (error) {
        console.error('[SignupForm] Failed to fetch IP:', error);
        await clientLogger.error(error, {
          componentName: 'SignupForm',
          action: 'fetchUserIP',
          feature: 'auth',
          userImpact: 'blocked'
        });
        // Continue without IP if fetch fails
      }
    };
    
    fetchIP();
  }, []);

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return "bg-red-500";
    if (passwordStrength.score <= 3) return "bg-yellow-500";
    if (passwordStrength.score <= 4) return "bg-blue-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (!formData.password) return "";
    if (passwordStrength.score <= 2) return "Weak";
    if (passwordStrength.score <= 3) return "Fair";
    if (passwordStrength.score <= 4) return "Good";
    return "Strong";
  };

  const validateForm = () => {
    const newValidation = {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      terms: ""
    };

    // First name validation
    if (!formData.firstName.trim()) {
      newValidation.firstName = "First name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newValidation.email = "Please enter a valid email address";
    }

    // Password validation
    if (formData.password.length < 8) {
      newValidation.password = "Password must be at least 8 characters";
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newValidation.confirmPassword = "Passwords do not match";
    }

    // Terms acceptance validation
    if (!termsAccepted) {
      newValidation.terms = "You must accept the Terms of Service and Privacy Policy";
    }

    setValidation(newValidation);
    return !newValidation.email && !newValidation.password && !newValidation.confirmPassword && !newValidation.firstName && !newValidation.terms;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[SignupForm] Form submitted');
    console.log('[SignupForm] Form data:', {
      email: formData.email,
      firstName: formData.firstName,
      hasPassword: !!formData.password,
      hasConfirmPassword: !!formData.confirmPassword,
      passwordMatch: formData.password === formData.confirmPassword,
      emailOptIn,
      timezone: defaultValues?.timezone
    });
    setError(null);

    if (!validateForm()) {
      console.log('[SignupForm] Form validation failed');
      console.log('[SignupForm] Validation state:', validation);
      return;
    }

    console.log('[SignupForm] Validation passed, calling server-side signup API with:', formData.email);
    setIsLoading(true);

    try {
      // Use the timezone passed in (from the quiz answer) — never the browser zone.
      const timezoneToUse = defaultValues?.timezone;

      // Call server-side signup API (same pattern as login)
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important: send cookies
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          metadata: {
            firstName: formData.firstName,
            marketingOptIn: emailOptIn,
            newsletterOptIn: emailOptIn,
            marketingOptInDate: emailOptIn ? new Date().toISOString() : null,
            termsAcceptedAt: new Date().toISOString(),
            privacyAcceptedAt: new Date().toISOString(),
            termsVersion: '2025-08-10',
            privacyVersion: '2025-08-10',
            ipAddress: userIP || undefined,
            ...(timezoneToUse && { timezone: timezoneToUse }),
            ...(actualReferralData && {
              isPaidReferrer: actualReferralData.isPaidReferrer,
              referralSource: actualReferralData.referralSource
            })
          }
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        const errorMessage = data.error || 'Signup failed';
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      // Load user profile data
      if (data.user) {
        await loadProfile(data.user.id);
      }

      if (onSuccess) {
        onSuccess();
        // Don't redirect here - let the parent component handle it
        // This ensures proper session establishment when redirecting
        setIsLoading(false);
        return;
      }

      // Only redirect if no onSuccess callback was provided
      // Use window.location.href for full page navigation to ensure middleware runs
      if (redirectTo) {
        console.log('[SignupForm] Signup successful, redirecting to:', redirectTo);
        window.location.href = redirectTo;
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      await clientLogger.error(err, {
        componentName: 'SignupForm',
        action: 'handleSubmit',
        metadata: { email: formData.email },
        feature: 'auth',
        userImpact: 'blocked'
      });

      setError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user types
    if (validation[name as keyof typeof validation]) {
      setValidation(prev => ({
        ...prev,
        [name]: ""
      }));
    }
    
    // Clear general error when user types
    if (error) setError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        <Input
          type="text"
          name="firstName"
          label="First name"
          value={formData.firstName}
          onChange={handleInputChange}
          placeholder="John"
          error={validation.firstName}
          required
          autoComplete="given-name"
        />

        <Input
          type="email"
          name="email"
          label="Email address"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="you@example.com"
          error={validation.email}
          required
          autoComplete="email"
        />

        <div>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              label="Password"
              value={formData.password}
              onChange={handleInputChange}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              placeholder="Create a strong password"
              error={validation.password}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          
          {/* Password Strength Indicator */}
          {formData.password && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600">Password strength:</span>
                <span className={`text-xs font-medium ${
                  passwordStrength.score <= 2 ? "text-red-600" :
                  passwordStrength.score <= 3 ? "text-yellow-600" :
                  passwordStrength.score <= 4 ? "text-blue-600" :
                  "text-green-600"
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      level <= passwordStrength.score
                        ? getPasswordStrengthColor()
                        : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Password Requirements */}
          {(passwordFocused || formData.password) && (
            <div className="mt-3 space-y-1.5">
              <p className="text-xs text-gray-600 font-medium mb-2">Password must contain:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  {passwordStrength.requirements.length ? (
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <span className={`text-xs ${
                    passwordStrength.requirements.length ? "text-green-700" : "text-gray-500"
                  }`}>
                    8+ characters
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordStrength.requirements.uppercase ? (
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <span className={`text-xs ${
                    passwordStrength.requirements.uppercase ? "text-green-700" : "text-gray-500"
                  }`}>
                    Uppercase letter
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordStrength.requirements.lowercase ? (
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <span className={`text-xs ${
                    passwordStrength.requirements.lowercase ? "text-green-700" : "text-gray-500"
                  }`}>
                    Lowercase letter
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordStrength.requirements.number ? (
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <span className={`text-xs ${
                    passwordStrength.requirements.number ? "text-green-700" : "text-gray-500"
                  }`}>
                    Number
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            label="Confirm password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Re-enter your password"
            error={validation.confirmPassword}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showConfirmPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
          {formData.confirmPassword && formData.password === formData.confirmPassword && (
            <Check className="absolute right-10 top-[38px] w-5 h-5 text-green-600" />
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-start">
            <input
              type="checkbox"
              id="terms"
              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (e.target.checked && validation.terms) {
                  setValidation(prev => ({ ...prev, terms: "" }));
                }
              }}
            />
            <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500 font-medium">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500 font-medium">
                Privacy Policy
              </a>
            </label>
          </div>
          {validation.terms && (
            <p className="mt-1 ml-6 text-xs text-red-600">{validation.terms}</p>
          )}
        </div>

        {/* Email Opt-in */}
        <div className="mt-4">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="emailOptin"
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                checked={emailOptIn}
                onChange={(e) => setEmailOptIn(e.target.checked)}
              />
              <label htmlFor="emailOptin" className="ml-3 cursor-pointer">
                <span className="block text-sm font-medium text-gray-900">
                  Yes, I want the good stuff! 🎉
                </span>
                <span className="block text-xs text-gray-600 mt-1">
                  Get exclusive mental health tips, early access to new features, and special member-only discounts delivered to your inbox
                </span>
              </label>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          isLoading={isLoading}
          disabled={!formData.email || !formData.password || !formData.confirmPassword || !formData.firstName || passwordStrength.score < 3}
        >
          {isLoading ? "Creating account..." : "Create account"}
        </Button>
      </div>
    </form>
  );
}